/**
 * AI 연결 (AI_PROTOCOL §12): 분석자료 만들기 → (사용자가 ChatGPT/Claude에서 실행) → 결과 붙여넣기 → 미리보기 → 반영.
 * - 붙여넣은 원문은 파싱 전에 먼저 저장한다 (실패해도 다시 요청할 필요 없음, §13).
 * - 반영은 결정적 id로 만들어 두 번 눌러도 중복되지 않는다. 반영 후에는 appliedAt으로 막는다.
 * - 모든 AI 결과는 actor = 'ai-import' (승인·확정 상태를 만들 수 없음: domain 규칙으로 강제).
 */
import { uuidv7 } from 'uuidv7';
import {
  FORMULATION_GROUPS,
  FORMULATION_LABELS,
  FORMULATION_ITEM_FIELDS,
  FORMULATION_LIST_FIELDS,
  nextProposal,
  parsePendingOpinions,
  pendingLine,
  type PendingKind,
} from '@/domain/workflow/formulation';
import {
  applyTidy,
  buildTidyPacket,
  parseTidy,
  planTidy,
  tidyCounts,
  tidySources,
  TIDY_FORMAT,
  TIDY_PACKET_VERSION,
  TIDY_TASK_VERSION,
  type TidyPlan,
  type TidyTarget,
} from '@/domain/ai/formulationTidy';
import type { AIImport, DocumentArtifact, RiskItem, Session } from '@/domain/schema/v1';
import { RISK_KEYS } from '@/domain/schema/v1';
import { buildApproachBackfillPacket, buildSecondReviewPacket, buildSessionPacket } from '@/domain/ai/packet';
import {
  parseApproachBackfill,
  parseSecondReview,
  parseSessionAnalysis,
  type ParsedApproachEvidence,
  type SecondReviewV1,
  type SessionAnalysisV1,
} from '@/domain/ai/parse';
import { AI_ROLE_CONFIDENCE_CAP, planImport, type ImportPlan } from '@/domain/ai/plan';
import { PROMPT_VERSIONS } from '@/domain/ai/prompts';
import { mergeProfile, PROFILE_FIELDS } from '@/domain/workflow/profileMerge';
import { recommendVerbatims } from '@/domain/transcript/excerpts';
import { buildCaseStatusFile, type KnowledgeFile } from '@/domain/ai/knowledge';
import { ageText } from '@/domain/workflow/age';
import {
  applyDocDraft,
  buildDocDraftPacket,
  DOC_DRAFT_SPECS,
  DOC_DRAFT_VERSIONS,
  draftFromMarkdown,
  fieldBody,
  fieldHeadings,
  parseDocDraft,
  type DocDraft,
  sectionsByHeadings,
} from '@/domain/ai/docDraft';
import {
  buildWholeDocPacket,
  DEFAULT_SUPERVISION_GUIDELINES,
  normalizeReportFormat,
  isWholeDocType,
  keyTerms,
  isLockedHeading,
  missingItems,
  prepareWholeDocRevise,
  type MustInclude,
  type WholeDocType,
} from '@/domain/ai/reportWriting';
import { isVerbatimLabel, parseDoc } from '@/domain/workflow/docModel';
import { isAiDraftMark } from '@/domain/ai/report';
import { RISK_LABELS } from '@/domain/workflow/brief';
import type { RiskKey } from '@/domain/schema/v1';

/** 위험 항목이 문서에 들어갔는지 찾을 말 */
const RISK_TERMS: Partial<Record<RiskKey, string[]>> = {
  suicidalIdeation: ['자살', '죽고 싶', '살고 싶지'],
  suicidalPlan: ['자살', '계획'],
  suicidalIntent: ['자살'],
  selfHarm: ['자해'],
  homicidalIdeation: ['타해', '해치'],
  violenceRisk: ['폭력'],
  abuseOrNeglect: ['학대', '방임'],
  substanceRisk: ['음주', '약물', '물질'],
};

import { TRANSCRIPT_LEGEND, transcriptLines } from '@/domain/ai/packet';
import { NAME_PLACEHOLDER } from '@/domain/workflow/documents';
import type { RepoContext, Repository } from '@/data/repo/repository';
import type { CounselingService } from './counseling';
import { stableId, TranscriptService } from './transcripts';
import { approachById, mergeApproachEvidence, secondaryOf } from '@/domain/workflow/approaches';
import {
  ASSESSMENT_PACKET_VERSION,
  buildAssessmentPacket,
  interpretationSummary,
  readInterpretation,
} from '@/domain/ai/assessment';
import { flatInterpretation } from '@/domain/workflow/documents';
import {
  buildSctReadPacket,
  parseSctItems,
  SCT_FORMAT,
  SCT_READ_VERSION,
  SCT_TEST_NAME,
  sctSummary,
} from '@/domain/assessment/sct';
import { JOURNAL_FORM_FIELDS } from '@/domain/schema/v1';
import { JOURNAL_FIELD_LABELS } from '@/domain/workflow/journal';
import type { ApproachEvidence } from '@/domain/schema/v1';

const toEvidence = (x: ParsedApproachEvidence, id: string): ApproachEvidence => ({
  id,
  approach: x.approach,
  kind: x.kind,
  quote: x.quote,
  ...(x.reason ? { reason: x.reason } : {}),
  ...(x.segmentIds.length ? { segmentIds: x.segmentIds } : {}),
});

export type ImportResult = { importId: string } & (
  { ok: true; plan: ImportPlan; warnings: string[] } | { ok: false; errors: string[] }
);

const byCreated = <T extends { createdAt: string }>(a: T, b: T) => a.createdAt.localeCompare(b.createdAt);

/** AI 분석 미리보기에서 '연결 못한 의견'마다 고른 처리 */
export type FormulationChoice =
  | { action: 'link'; id: string }
  | { action: 'new' }
  | { action: 'protective' }
  | { action: 'discard' }
  | { action: 'later' };

/** 개정안 변경 이유: 기존 내용 + AI 근거 + 연결 못한 의견(한 줄씩, 이미 있으면 다시 넣지 않음) */
function mergeChangeReason(
  prev: string | undefined,
  reason: string | undefined,
  unmatched: { kind: PendingKind; text: string }[],
): string {
  const lines = (prev ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const have = new Set(lines);
  const add = (l: string) => {
    if (!have.has(l)) {
      have.add(l);
      lines.push(l);
    }
  };
  if (reason?.trim()) add(`AI 근거: ${reason.trim()}`);
  for (const u of unmatched) add(pendingLine(u.kind, u.text));
  return lines.join('\n') || 'AI 제안 반영';
}

export class AiBridge {
  private readonly ts: TranscriptService;
  constructor(
    private readonly repo: Repository,
    ctx: RepoContext,
    private readonly svc: CounselingService,
  ) {
    this.ts = new TranscriptService(repo, ctx);
  }

  // ---------------- 분석자료 ----------------

  async sessionPacket(sessionId: string): Promise<string> {
    const session = await this.svc.getSession(sessionId);
    const client = await this.svc.getClient(session.clientId);
    const [intake, goals, formulation, diagnosis, memo, sessions, risks, evidence, working] = await Promise.all([
      this.svc.getIntake(client.id),
      this.svc.listGoals(client.id),
      this.svc.currentFormulation(client.id),
      this.svc.currentDiagnosis(client.id),
      this.svc.getMemo(sessionId),
      this.svc.listSessions(client.id),
      this.repo.list('RiskAssessment', { clientId: client.id }),
      this.repo.list('ClinicalEvidence', { clientId: client.id }),
      this.ts.getWorking(session),
    ]);
    const prior = sessions.filter((s) => s.status === 'approved' && s.sessionNumber < session.sessionNumber).slice(-3);
    const recent = await Promise.all(
      prior.map(async (s) => {
        const [j, a] = await Promise.all([this.svc.currentJournal(s.id), this.svc.getAnalysis(s.id)]);
        return {
          sessionNumber: s.sessionNumber,
          sessionDate: s.sessionDate,
          summary: a?.sessionSummary ?? j?.importantIssue ?? '(요약 없음)',
        };
      }),
    );
    const latestRisk = risks
      .filter((r) => r.sessionId !== sessionId || r.counselorApproved)
      .sort(byCreated)
      .at(-1);
    const previousBrief = await this.svc.previousBriefFor(session);
    return buildSessionPacket({
      client,
      session,
      goals,
      recent,
      priorClientReports: evidence
        .filter(
          (e) =>
            e.evidenceType === 'CLIENT_REPORT' && !e.needsVerification && e.sessionId !== sessionId && !e.deletedAt,
        )
        .slice(-15),
      ...(intake ? { intake } : {}),
      ...(formulation ? { formulation } : {}),
      ...(diagnosis ? { diagnosis } : {}),
      ...(latestRisk ? { latestRisk } : {}),
      ...(previousBrief ? { previousBrief } : {}),
      ...(working ? { working } : {}),
      ...(memo?.text ? { memoText: memo.text } : {}),
      ...(working?.segments.length
        ? {
            screenWindows: recommendVerbatims(
              working.segments.map((s) => ({ ...s, startMs: s.startMs ?? 0 })),
              { minutes: 12 },
            ),
          }
        : {}),
      approachScores: (await this.svc.approachBoard(client.id)).scores,
      assessments: (await this.svc.listAssessments(client.id)).map((a) => ({
        testName: a.testName,
        ...(a.administeredAt ? { administeredAt: a.administeredAt } : {}),
        ...(a.scoresSummary ? { summary: a.scoresSummary } : {}),
        ...(a.interpretation ? { interpretation: interpretationSummary(a.interpretation)! } : {}),
        approved: !!a.interpretationApprovedAt,
      })),
    });
  }

  // ---------------- 심리검사 해석 (A35) ----------------

  async assessmentPacket(assessmentId: string, request?: string): Promise<string> {
    const a = await this.repo.get('PsychologicalAssessment', assessmentId);
    const [c, intake] = await Promise.all([this.svc.getClient(a.clientId), this.svc.getIntake(a.clientId)]);
    const age = c.birthYear
      ? ageText({ birthYear: c.birthYear, on: new Date().toISOString().slice(0, 10) })
      : c.ageRange;
    const main = approachById(c.treatmentPlan?.approach ?? '');
    return this.sanitize(
      buildAssessmentPacket({
        clientCode: c.clientCode,
        ageText: age,
        gender: c.gender,
        assessment: a,
        context: {
          ...(intake?.presentingProblems ? { presentingProblems: intake.presentingProblems } : {}),
          ...(main ? { mainTheory: `${main.name} (${main.scholar})` } : {}),
          ...(request ? { request } : {}),
        },
      }),
      a.clientId,
    );
  }

  // ---------------- 문장완성검사 옮겨 적기 (A42) ----------------

  /** 검사지 사진·PDF와 함께 외부 AI에 보낼 요청 (문항 목록이 있으면 기준으로) */
  async sctReadPacket(clientId: string): Promise<string> {
    const [c, settings] = await Promise.all([this.svc.getClient(clientId), this.svc.getSettings()]);
    return buildSctReadPacket({ clientCode: c.clientCode, stems: settings?.sctStems });
  }

  /** 옮겨 적은 답 가져오기: 원문은 AIImport에, 응답은 '확인 전'으로 새 검사 기록에 */
  async importSctItems(clientId: string, raw: string, provider: AIImport['provider'] = 'claude') {
    const stems = (await this.svc.getSettings())?.sctStems ?? [];
    const parsed = parseSctItems(raw, stems);
    const imp = await this.repo.create(
      'AIImport',
      {
        clientId,
        provider,
        purpose: 'other',
        packetVersion: SCT_READ_VERSION,
        outputFormatVersion: SCT_FORMAT,
        promptVersion: SCT_READ_VERSION,
        rawOutput: raw,
        parseStatus: parsed.ok ? 'parsed' : 'failed',
        ...(parsed.ok
          ? parsed.warnings.length
            ? { parseWarnings: parsed.warnings }
            : {}
          : { parseErrors: parsed.errors }),
      },
      'ai-import',
    );
    if (!parsed.ok) throw new Error(`읽지 못했습니다: ${parsed.errors.join(', ')}`);
    const a = await this.repo.create(
      'PsychologicalAssessment',
      {
        clientId,
        testName: SCT_TEST_NAME,
        testKind: 'sct',
        source: 'counselor',
        ...(parsed.value.administeredAt ? { administeredAt: parsed.value.administeredAt } : {}),
        items: parsed.value.items,
        itemsSource: 'external-ai',
        scoresSummary: sctSummary(parsed.value.items),
      },
      'ai-import',
    );
    await this.repo.update('AIImport', imp.id, { appliedAt: new Date().toISOString() }, 'ai-import');
    return { assessment: a, warnings: parsed.warnings };
  }

  /** 해석 답 가져오기: 원문은 AIImport에, 해석은 확인 전 초안으로 */
  async importAssessmentInterpretation(assessmentId: string, raw: string, provider: AIImport['provider'] = 'claude') {
    const a = await this.repo.get('PsychologicalAssessment', assessmentId);
    const r = readInterpretation(raw);
    await this.repo.create(
      'AIImport',
      {
        clientId: a.clientId,
        provider,
        purpose: 'other',
        packetVersion: ASSESSMENT_PACKET_VERSION,
        outputFormatVersion: 'ASSESSMENT_INTERPRETATION_V1',
        promptVersion: ASSESSMENT_PACKET_VERSION,
        rawOutput: raw,
        parseStatus: r.ok ? 'parsed' : 'failed',
        ...(r.ok ? {} : { parseErrors: [r.error] }),
      },
      'ai-import',
    );
    if (!r.ok) throw new Error(r.error);
    return this.repo.update(
      'PsychologicalAssessment',
      assessmentId,
      { interpretation: r.text, interpretationSource: 'external-ai', interpretationApprovedAt: undefined },
      'ai-import',
    );
  }

  // ---------------- 이전 회기 치료 접근 근거 (A33) ----------------

  /** 처리된 회기 중 근거를 아직 받지 않은 회기 (AI 결과에 근거 항목이 없던 회기) */
  async approachBackfillPending(clientId: string): Promise<number[]> {
    const out: number[] = [];
    for (const s of await this.svc.listSessions(clientId)) {
      if (s.status === 'draft' || s.status === 'processing') continue;
      const a = await this.svc.getAnalysis(s.id);
      if (a?.approachEvidence) continue;
      if ((await this.ts.getWorking(s))?.segments.length) out.push(s.sessionNumber);
    }
    return out;
  }

  /** 한 번에 보낼 자료: 근거가 없는 회기를 최대 4개씩 (전사가 길어 나눔) */
  async approachBackfillPacket(
    clientId: string,
    max = 4,
  ): Promise<{ packet: string; sessionNumbers: number[]; remaining: number } | null> {
    const pending = await this.approachBackfillPending(clientId);
    if (!pending.length) return null;
    const batch = pending.slice(0, max);
    const client = await this.svc.getClient(clientId);
    const sessions = (await this.svc.listSessions(clientId)).filter((s) => batch.includes(s.sessionNumber));
    const [intake, formulation, board] = await Promise.all([
      this.svc.getIntake(clientId),
      this.svc.currentFormulation(clientId),
      this.svc.approachBoard(clientId),
    ]);
    const items = await Promise.all(
      sessions.map(async (s) => {
        const [w, a] = await Promise.all([this.ts.getWorking(s), this.svc.getAnalysis(s.id)]);
        return {
          sessionNumber: s.sessionNumber,
          sessionDate: s.sessionDate,
          ...(a?.sessionSummary ? { summary: a.sessionSummary } : {}),
          segments: w?.segments ?? [],
        };
      }),
    );
    const packet = buildApproachBackfillPacket({
      client,
      ...(intake?.presentingProblems ? { presentingProblems: intake.presentingProblems } : {}),
      ...(formulation ? { formulation } : {}),
      approachScores: board.scores,
      sessions: items.sort((a, b) => a.sessionNumber - b.sessionNumber),
    });
    return {
      packet: await this.sanitize(packet, clientId),
      sessionNumbers: batch,
      remaining: pending.length - batch.length,
    };
  }

  /** 붙여넣은 답을 회기별로 나눠 넣는다. 원문은 AIImport에 보존. 받은 회기는 근거가 없어도 빈 목록으로 표시 (다시 묻지 않음) */
  async importApproachBackfill(
    clientId: string,
    raw: string,
    provider: AIImport['provider'] = 'claude',
  ): Promise<{ sessions: number[]; items: number; warnings: string[] }> {
    const parsed = parseApproachBackfill(raw);
    const imp = await this.repo.create(
      'AIImport',
      {
        clientId,
        provider,
        purpose: 'other',
        packetVersion: PROMPT_VERSIONS.approachBackfillPacket,
        outputFormatVersion: PROMPT_VERSIONS.approachEvidence,
        promptVersion: PROMPT_VERSIONS.approachBackfillTask,
        rawOutput: raw,
        parseStatus: parsed.ok ? 'parsed' : 'failed',
        ...(parsed.ok
          ? parsed.warnings.length
            ? { parseWarnings: parsed.warnings }
            : {}
          : { parseErrors: parsed.errors }),
      },
      'ai-import',
    );
    if (!parsed.ok) throw new Error(`읽지 못했습니다: ${parsed.errors.join(', ')}`);
    const warnings = [...parsed.warnings];
    const sessions = await this.svc.listSessions(clientId);
    const base = Date.parse(imp.createdAt);
    const done: number[] = [];
    let count = 0;
    for (const entry of parsed.value.sessions) {
      const s = sessions.find((x) => x.sessionNumber === entry.sessionNumber);
      if (!s) {
        warnings.push(`${entry.sessionNumber}회기가 없어 건너뜀`);
        continue;
      }
      const segs = (await this.ts.getWorking(s))?.segments ?? [];
      const ids = new Set(segs.map((x) => x.segmentId));
      const resolve = (id: string) =>
        ids.has(id) ? [id] : segs.filter((x) => x.segmentId.startsWith(`${id}-`)).map((x) => x.segmentId);
      const items = await Promise.all(
        entry.items.map(async (x, i) =>
          toEvidence(
            { ...x, segmentIds: [...new Set(x.segmentIds.flatMap(resolve))] },
            await stableId(`ai|${imp.id}|s${s.sessionNumber}|ae${i}`, base),
          ),
        ),
      );
      const a = await this.svc.getAnalysis(s.id);
      if (a)
        await this.repo.update(
          'SessionAnalysis',
          a.id,
          { approachEvidence: mergeApproachEvidence(a.approachEvidence, items) },
          'ai-import',
        );
      else
        await this.repo.create(
          'SessionAnalysis',
          {
            clientId,
            sessionId: s.id,
            formatVersion: 'SESSION_ANALYSIS_V1',
            source: provider === 'other' ? 'manual' : provider,
            approachEvidence: items,
          },
          'ai-import',
        );
      done.push(s.sessionNumber);
      count += items.length;
    }
    await this.repo.update('AIImport', imp.id, { appliedAt: new Date().toISOString() }, 'ai-import');
    return { sessions: done, items: count, warnings };
  }

  // ---------------- 사례개념화 정리 (A38) ----------------

  /** 정리할 내용: 진행 중인 개정안, 없으면 현재 버전에서 새로 만들 개정안 (만들지는 않음 — 넣을 때 만든다) */
  private async tidyTarget(clientId: string): Promise<{ target: TidyTarget; version: number; openId?: string } | null> {
    const revisions = await this.svc.listFormulationRevisions(clientId);
    const open = revisions.find((r) => r.status === 'proposal');
    if (open) return { target: open, version: open.version, openId: open.id };
    const prev = (await this.svc.currentFormulation(clientId)) ?? revisions.at(-1);
    const version = (revisions.at(-1)?.version ?? 0) + 1;
    // 아직 사례개념화가 없으면 빈 개정안에서 시작 (지난 회기 분석으로 처음 채우기)
    if (!prev) return { target: {}, version };
    const content = nextProposal(prev, { clientId, formulationId: prev.formulationId, version, basedOnSessionIds: [] });
    // 정리 못한 채 승인한 AI 의견도 함께 정리한다 (새 개정안에 다시 남김)
    const pending = parsePendingOpinions(prev.changeReason).map((o) => o.line);
    return { target: { ...content, ...(pending.length ? { changeReason: pending.join('\n') } : {}) }, version };
  }

  /** 정리 요청 자료: 지금 항목 수와 함께. 정리할 것도, 채울 근거(분석한 회기)도 없으면 null */
  async formulationTidyPacket(
    clientId: string,
    request?: string,
    /** rebuild 'auto': 사례개념화가 비어 있고 전사가 있으면 다시 세우기 */
    opts: { rebuild?: boolean | 'auto' } = {},
  ): Promise<{
    packet: string;
    version: number;
    counts: ReturnType<typeof tidyCounts>;
    /** 전사를 보낸 회기 (다시 세우기) */
    transcriptSessions: number[];
    /** 실제로 고른 방식 (다시 세우기인지) */
    rebuild: boolean;
    /** 전사가 있는 회기 수 (다시 세우기를 고를 수 있는지) */
    withTranscript: number;
  } | null> {
    const t = await this.tidyTarget(clientId);
    if (!t) return null;
    const counts = tidyCounts(tidySources(t.target));
    const client = await this.svc.getClient(clientId);
    const intake = await this.svc.getIntake(clientId);
    const sessions = await this.svc.listSessions(clientId);
    const all = await Promise.all(
      sessions.map(async (s) => {
        const a = await this.svc.getAnalysis(s.id);
        const texts = (x?: { text: string }[]) => (x ?? []).map((y) => y.text).filter((t) => t.trim());
        const notes = [
          { label: '주요 사건', items: a?.keyEvents ?? [] },
          { label: '변화', items: a?.notableChanges ?? [] },
          { label: '정서', items: texts(a?.emotions) },
          { label: '인지', items: texts(a?.cognitions) },
          { label: '행동', items: texts(a?.behaviors) },
          { label: '관계', items: texts(a?.relationshipPatterns) },
        ].filter((n) => n.items.length);
        return {
          sessionNumber: s.sessionNumber,
          sessionDate: s.sessionDate,
          ...(a?.sessionSummary ? { summary: a.sessionSummary } : {}),
          ...(notes.length ? { notes } : {}),
        };
      }),
    );
    // 분석이 있는 회기만, 최근 8개 (전사를 넣기 전 회기는 근거가 없음)
    const summaries = all.filter((s) => s.summary || s.notes).slice(-8);
    // 다시 세우기: 전사가 있는 회기의 전사 (최근 6회기까지 — 그 이상은 너무 길어 요약만)
    const withSegs = (
      await Promise.all(sessions.map(async (s) => ({ s, segs: (await this.ts.getWorking(s))?.segments ?? [] })))
    ).filter((x) => x.segs.length);
    const empty = !counts.items && !counts.lists && !counts.opinions;
    const rebuild = opts.rebuild === 'auto' ? empty && withSegs.length > 0 : !!opts.rebuild;
    const transcripts = rebuild
      ? withSegs.slice(-6).map(({ s, segs }) => ({
          sessionNumber: s.sessionNumber,
          sessionDate: s.sessionDate,
          lines: transcriptLines(segs),
        }))
      : [];
    if (empty && !summaries.length && !withSegs.length) return null;
    const packet = buildTidyPacket({
      clientCode: client.clientCode,
      version: t.version,
      ...(intake?.presentingProblems?.length ? { presentingProblems: intake.presentingProblems } : {}),
      sessions: summaries,
      target: t.target,
      ...(transcripts.length ? { transcripts } : {}),
      ...(request ? { request } : {}),
    });
    return {
      packet: await this.sanitize(packet, clientId),
      version: t.version,
      counts,
      transcriptSessions: transcripts.map((x) => x.sessionNumber),
      withTranscript: withSegs.length,
      rebuild,
    };
  }

  /** 붙여넣은 답: 원문을 먼저 저장하고 미리보기를 돌려준다 */
  async importFormulationTidy(
    clientId: string,
    raw: string,
    provider: AIImport['provider'] = 'claude',
  ): Promise<
    { importId: string } & ({ ok: true; plan: TidyPlan; warnings: string[] } | { ok: false; errors: string[] })
  > {
    const parsed = parseTidy(raw);
    const imp = await this.repo.create(
      'AIImport',
      {
        clientId,
        provider,
        purpose: 'case-formulation',
        packetVersion: TIDY_PACKET_VERSION,
        outputFormatVersion: TIDY_FORMAT,
        promptVersion: TIDY_TASK_VERSION,
        rawOutput: raw,
        parseStatus: parsed.ok ? 'parsed' : 'failed',
        ...(parsed.ok
          ? parsed.warnings.length
            ? { parseWarnings: parsed.warnings }
            : {}
          : { parseErrors: parsed.errors }),
      },
      'ai-import',
    );
    if (!parsed.ok) return { importId: imp.id, ok: false, errors: parsed.errors };
    const t = await this.tidyTarget(clientId);
    if (!t) return { importId: imp.id, ok: false, errors: ['정리할 사례개념화가 없습니다'] };
    return { importId: imp.id, ok: true, plan: planTidy(t.target, parsed.value), warnings: parsed.warnings };
  }

  /** 미리보기에서 고른 줄만 개정안에 넣는다 (개정안이 없으면 현재 버전에서 만든다). 두 번 눌러도 한 번만 */
  async applyFormulationTidy(importId: string, choice: { rows: number[]; drops: number[]; dropUntouched?: boolean }) {
    const imp = await this.repo.get('AIImport', importId);
    if (imp.appliedAt) throw new Error('이미 넣은 정리입니다');
    const parsed = parseTidy(imp.rawOutput);
    if (!parsed.ok) throw new Error(parsed.errors.join(', '));
    const clientId = imp.clientId;
    const t = await this.tidyTarget(clientId);
    if (!t) throw new Error('정리할 사례개념화가 없습니다');
    const plan = planTidy(t.target, parsed.value);
    const prop = t.openId
      ? await this.svc.getFormulationRevision(t.openId)
      : await this.svc.createFormulationProposal(clientId, []);
    const base = t.openId ? prop : { ...prop, changeReason: t.target.changeReason };
    const { patch, merged, dropped } = applyTidy(base, plan, choice, uuidv7);
    const rev = await this.svc.updateFormulation(prop.id, patch);
    await this.repo.update('AIImport', imp.id, { appliedAt: new Date().toISOString() }, 'ai-import');
    return { revision: rev, merged, dropped };
  }

  async markPacketCopied(sessionId: string) {
    const s = await this.svc.getSession(sessionId);
    if (!s.processingState.aiPacketCreated) {
      await this.repo.update(
        'Session',
        sessionId,
        { processingState: { ...s.processingState, aiPacketCreated: true } },
        'system',
      );
    }
  }

  // ---------------- 결과 가져오기 ----------------

  async importSessionAnalysis(
    sessionId: string,
    raw: string,
    provider: AIImport['provider'] = 'claude',
  ): Promise<ImportResult> {
    const session = await this.svc.getSession(sessionId);
    // 1) 원문부터 보존
    const imp = await this.repo.create(
      'AIImport',
      {
        clientId: session.clientId,
        sessionId,
        provider,
        purpose: 'session-analysis',
        packetVersion: PROMPT_VERSIONS.sessionPacket,
        outputFormatVersion: PROMPT_VERSIONS.sessionAnalysis,
        promptVersion: PROMPT_VERSIONS.sessionTask,
        rawOutput: raw,
        parseStatus: 'pending',
      },
      'user',
    );
    // 2) 파싱
    const parsed = parseSessionAnalysis(raw);
    if (!parsed.ok) {
      await this.repo.update('AIImport', imp.id, { parseStatus: 'failed', parseErrors: parsed.errors }, 'system');
      return { importId: imp.id, ok: false, errors: parsed.errors };
    }
    const plan = await this.plan(session, parsed.value);
    const warnings = [...parsed.warnings, ...plan.warnings];
    if (parsed.value.sessionNumber !== undefined && parsed.value.sessionNumber !== session.sessionNumber) {
      warnings.unshift(
        `결과의 회기 번호(${parsed.value.sessionNumber})가 이 회기(${session.sessionNumber})와 다릅니다. 다른 회기의 결과가 아닌지 확인하세요.`,
      );
    }
    await this.repo.update(
      'AIImport',
      imp.id,
      {
        parseStatus: 'parsed',
        ...(warnings.length ? { parseWarnings: warnings } : {}),
        roleReviewProposals: parsed.value.roleReviewProposals,
      },
      'system',
    );
    return { importId: imp.id, ok: true, plan, warnings };
  }

  private async plan(session: Session, a: SessionAnalysisV1): Promise<ImportPlan> {
    const [working, goals, formulation, journal] = await Promise.all([
      this.ts.getWorking(session),
      this.svc.listGoals(session.clientId),
      this.svc.currentFormulation(session.clientId),
      this.svc.currentJournal(session.id),
    ]);
    return planImport(a, {
      segments: working?.segments ?? [],
      goals,
      journal,
      newId: uuidv7,
      ...(formulation ? { formulation } : {}),
    });
  }

  /** 저장된 결과의 반영 미리보기 (화면을 떠났다 돌아와도 다시 붙여넣을 필요 없음) */
  async previewImport(importId: string): Promise<{ plan: ImportPlan; warnings: string[] } | null> {
    const imp = await this.repo.get('AIImport', importId);
    if (!imp.sessionId || imp.purpose !== 'session-analysis') return null;
    const parsed = parseSessionAnalysis(imp.rawOutput);
    if (!parsed.ok) return null;
    const plan = await this.plan(await this.svc.getSession(imp.sessionId), parsed.value);
    // 예전 버전이 저장한 '가설 연결 못함' 경고는 이제 미리보기에서 직접 고르므로 빼고 보여준다
    const stored = (imp.parseWarnings ?? []).filter(
      (w) => !plan.warnings.includes(w) && !/가설을 기존 가설과 연결하지 못함/.test(w),
    );
    return { plan, warnings: [...stored, ...plan.warnings] };
  }

  async listImports(sessionId: string) {
    return (await this.repo.list('AIImport', { sessionId })).sort(byCreated);
  }

  /** 반영 (여러 번 눌러도 한 번만) */
  async applyImport(
    importId: string,
    opts: { formulationChoices?: Record<number, FormulationChoice> } = {},
  ): Promise<{ applied: boolean; warnings: string[] }> {
    const imp = await this.repo.get('AIImport', importId);
    if (imp.appliedAt) return { applied: false, warnings: ['이미 반영된 결과입니다'] };
    if (imp.parseStatus !== 'parsed' || !imp.sessionId) throw new Error('파싱되지 않은 결과는 반영할 수 없습니다');
    const parsed = parseSessionAnalysis(imp.rawOutput);
    if (!parsed.ok) throw new Error('결과를 다시 읽지 못했습니다');
    let session = await this.svc.getSession(imp.sessionId);
    const plan = await this.plan(session, parsed.value);
    const base = Date.parse(imp.createdAt);
    const id = (k: string) => stableId(`ai|${importId}|${k}`, base);
    const provider = imp.provider === 'other' ? 'manual' : imp.provider;

    // 회기 분석: 기존 분석(상담자 작성 포함)이 있으면 빈 칸만 채우고 목록은 덧붙인다
    const warnings = [...plan.warnings];
    const existing = await this.svc.getAnalysis(session.id);
    const nt = (t: string) => t.replace(/\s+/g, '');
    const addItems = <X extends { text: string }>(cur: X[] | undefined, add: X[]) => {
      const seen = new Set((cur ?? []).map((x) => nt(x.text)));
      return [...(cur ?? []), ...add.filter((x) => !seen.has(nt(x.text)))];
    };
    const addStrs = (cur: string[] | undefined, add: string[]) => [...new Set([...(cur ?? []), ...add])];
    const ivs = await Promise.all(plan.analysis.interventions.map(async (x, i) => ({ id: await id(`iv${i}`), ...x })));
    // 치료 접근 근거 (A33): 새 결과로 바꾸되 상담자가 뺀 근거는 계속 뺀 채로
    const approachEvidence = plan.approachEvidence.length
      ? mergeApproachEvidence(
          existing?.approachEvidence,
          await Promise.all(plan.approachEvidence.map(async (x, i) => toEvidence(x, await id(`ae${i}`)))),
        )
      : undefined;
    if (existing) {
      const seenIv = new Set((existing.interventions ?? []).map((x) => nt(x.interventionName)));
      await this.repo.update(
        'SessionAnalysis',
        existing.id,
        {
          ...(!existing.sessionSummary?.trim() && plan.analysis.sessionSummary
            ? { sessionSummary: plan.analysis.sessionSummary }
            : {}),
          keyEvents: addStrs(existing.keyEvents, plan.analysis.keyEvents),
          notableChanges: addStrs(existing.notableChanges, plan.analysis.notableChanges),
          emotions: addItems(existing.emotions, plan.analysis.emotions),
          cognitions: addItems(existing.cognitions, plan.analysis.cognitions),
          behaviors: addItems(existing.behaviors, plan.analysis.behaviors),
          relationshipPatterns: addItems(existing.relationshipPatterns, plan.analysis.relationshipPatterns),
          interventions: [...(existing.interventions ?? []), ...ivs.filter((x) => !seenIv.has(nt(x.interventionName)))],
          ...(approachEvidence ? { approachEvidence } : {}),
        },
        'ai-import',
      );
    } else {
      await this.repo.create(
        'SessionAnalysis',
        {
          clientId: session.clientId,
          sessionId: session.id,
          formatVersion: 'SESSION_ANALYSIS_V1',
          source: provider,
          ...(plan.analysis.sessionSummary ? { sessionSummary: plan.analysis.sessionSummary } : {}),
          keyEvents: plan.analysis.keyEvents,
          notableChanges: plan.analysis.notableChanges,
          emotions: plan.analysis.emotions,
          cognitions: plan.analysis.cognitions,
          behaviors: plan.analysis.behaviors,
          relationshipPatterns: plan.analysis.relationshipPatterns,
          interventions: ivs,
          ...(approachEvidence ? { approachEvidence } : {}),
        },
        'ai-import',
        { id: await id('analysis') },
      );
    }

    // 근거: 같은 회기에 결과를 다시 넣으면 갱신 — 이전 결과의 AI 근거 중 상담자가 손대지 않은 것(확인 안 함·수정 안 함)은 새 결과로 바꾼다
    const prevEvidence = (await this.repo.list('ClinicalEvidence', { sessionId: session.id })).filter(
      (e) => !e.deletedAt,
    );
    const newTexts = new Set(plan.evidence.map((e) => nt(e.text)));
    let replacedEvidence = 0;
    for (const e of prevEvidence) {
      const untouchedAi = e.author === 'ai' && e.createdBy === 'ai-import' && e.revision === 1 && !e.verified;
      if (untouchedAi && !newTexts.has(nt(e.text))) {
        await this.repo.softDelete('ClinicalEvidence', e.id);
        replacedEvidence++;
      }
    }
    const keptTexts = new Set(
      prevEvidence
        .filter((e) => e.revision > 1 || e.author !== 'ai' || newTexts.has(nt(e.text)))
        .map((e) => nt(e.text)),
    );
    if (replacedEvidence)
      warnings.push(`이전 결과의 AI 근거 ${replacedEvidence}개를 새 결과로 바꿨습니다 (확인·수정하신 근거는 그대로)`);
    for (const [i, e] of plan.evidence.entries()) {
      const eid = await id(`ev${i}`);
      if (await this.repo.find('ClinicalEvidence', eid)) continue;
      if (keptTexts.has(nt(e.text))) continue; // 같은 문장이 이미 있음
      await this.repo.create(
        'ClinicalEvidence',
        {
          clientId: session.clientId,
          sessionId: session.id,
          sourceSession: session.sessionNumber,
          evidenceType: e.evidenceType,
          text: e.text,
          ...(e.sourceSegmentIds?.length ? { sourceSegmentIds: e.sourceSegmentIds } : {}),
          ...(e.sourceTimeMs !== undefined ? { sourceTimeMs: Math.max(0, Math.round(e.sourceTimeMs)) } : {}),
          ...(e.roleAtExtraction ? { roleAtExtraction: e.roleAtExtraction } : {}),
          ...(e.roleConfidenceAtExtraction !== undefined
            ? { roleConfidenceAtExtraction: e.roleConfidenceAtExtraction }
            : {}),
          ...(e.evidenceType === 'CLIENT_REPORT' && e.agreementStrength
            ? { agreementStrength: e.agreementStrength }
            : {}),
          verified: false,
          needsVerification: e.needsVerification,
          author: 'ai',
          source: 'ai-analysis',
        },
        'ai-import',
        { id: eid },
      );
    }

    // 위험도 제안 (승인 안 된 기록)
    if (plan.risk) {
      const current = (await this.repo.list('RiskAssessment', { sessionId: session.id })).sort(byCreated).at(-1);
      const items = Object.fromEntries(
        RISK_KEYS.map((k) => {
          const prev: RiskItem = current?.[k] ?? { status: 'not_assessed' };
          const p = plan.risk![k];
          if (!p) return [k, prev];
          if (prev.status !== 'not_assessed' && prev.status !== p.status) {
            warnings.push(`위험(${k}): 입력된 값(${prev.status})을 유지했습니다. AI 제안은 ${p.status}입니다.`);
            return [k, p.followUpRequired ? { ...prev, followUpRequired: true } : prev];
          }
          return [k, { ...prev, status: p.status, ...(p.followUpRequired ? { followUpRequired: true } : {}) }];
        }),
      ) as Record<(typeof RISK_KEYS)[number], RiskItem>;
      if (current && !current.counselorApproved)
        await this.repo.update('RiskAssessment', current.id, items, 'ai-import');
      else if (!(await this.repo.find('RiskAssessment', await id('risk'))))
        await this.repo.create(
          'RiskAssessment',
          { clientId: session.clientId, sessionId: session.id, ...items, counselorApproved: false },
          'ai-import',
          { id: await id('risk') },
        );
    }

    // 목표 진행 제안: 같은 회기·목표에 승인 전 AI 제안이 있으면 그걸 갱신
    const prevProgress = (await this.repo.list('GoalProgress', { sessionId: session.id })).filter(
      (p) => !p.deletedAt && !p.approvedStatus,
    );
    for (const g of plan.goals) {
      const gid = await id(`goal-${g.goalId}`);
      if (await this.repo.find('GoalProgress', gid)) continue;
      const prev = prevProgress.find((p) => p.goalId === g.goalId && p.createdBy === 'ai-import');
      if (prev) {
        await this.repo.update(
          'GoalProgress',
          prev.id,
          {
            ...(g.to !== g.from ? { proposedStatus: g.to as never } : {}),
            ...(g.note ? { progressNote: g.note } : {}),
          },
          'ai-import',
        );
        continue;
      }
      await this.repo.create(
        'GoalProgress',
        {
          clientId: session.clientId,
          goalId: g.goalId,
          sessionId: session.id,
          ...(g.to !== g.from ? { proposedStatus: g.to as never } : {}),
          ...(g.note ? { progressNote: g.note } : {}),
        },
        'ai-import',
        { id: gid },
      );
    }

    // 사례개념화 개정안 (연결 못한 AI 의견은 개정안에 남겨 사례개념화 화면에서 정리)
    if (plan.formulation) {
      const prop = await this.svc.createFormulationProposal(session.clientId, [session.id]);
      const have = new Set((prop.beliefHypotheses ?? []).map((h) => nt(h.text)));
      const added = plan.formulation.addedTexts
        .filter((t) => !have.has(nt(t)))
        .map((text) => ({ id: uuidv7(), text, status: 'hypothesis' as const }));
      const uniq = (a?: string[], b: string[] = []) => [...new Set([...(a ?? []), ...b])];
      // 기존 가설과 연결 못한 의견: 미리보기에서 고른 대로 (고르지 않은 것은 개정안에 남겨 사례개념화 탭에서 정리)
      const linked = { strengthened: [] as string[], weakened: [] as string[], rejected: [] as string[] };
      const protective: string[] = [];
      const addedOther: string[] = [];
      const pending: typeof plan.formulation.unmatched = [];
      const live = new Set(FORMULATION_ITEM_FIELDS.flatMap((f) => prop[f] ?? []).map((h) => h.id));
      plan.formulation.unmatched.forEach((u, i) => {
        const c = opts.formulationChoices?.[i];
        if (c?.action === 'link' && live.has(c.id)) linked[u.kind].push(c.id);
        else if (c?.action === 'new' && u.kind !== 'rejected' && !have.has(nt(u.text))) {
          const h = { id: uuidv7(), text: u.text, status: 'hypothesis' as const };
          added.push(h);
          have.add(nt(u.text));
          linked[u.kind].push(h.id);
        } else if (c?.action === 'protective') protective.push(u.text);
        else if (c?.action !== 'discard') pending.push(u);
      });
      // 칸별 새 항목: 목록 칸은 문장으로, 항목 칸은 가설로 (새 항목은 '추가'로 표시)
      const fieldAdds: Record<string, unknown> = {};
      for (const [k, texts] of Object.entries(plan.formulation.addedItems)) {
        if ((FORMULATION_LIST_FIELDS as readonly string[]).includes(k)) {
          const cur = (prop[k as (typeof FORMULATION_LIST_FIELDS)[number]] ?? []) as string[];
          fieldAdds[k] = uniq(cur, k === 'protectiveFactors' ? [...texts, ...protective] : texts);
        } else if ((FORMULATION_ITEM_FIELDS as readonly string[]).includes(k) && k !== 'beliefHypotheses') {
          const cur = prop[k as (typeof FORMULATION_ITEM_FIELDS)[number]] ?? [];
          const seen = new Set(cur.map((h) => nt(h.text)));
          const items = texts
            .filter((t) => !seen.has(nt(t)))
            .map((text) => ({ id: uuidv7(), text, status: 'hypothesis' as const }));
          fieldAdds[k] = [...cur, ...items];
          addedOther.push(...items.map((h) => h.id));
        }
      }
      await this.repo.update(
        'CaseFormulationRevision',
        prop.id,
        {
          ...(plan.formulation.currentProblems ? { currentProblems: plan.formulation.currentProblems } : {}),
          ...(protective.length ? { protectiveFactors: uniq(prop.protectiveFactors, protective) } : {}),
          ...fieldAdds,
          beliefHypotheses: [...(prop.beliefHypotheses ?? []), ...added],
          addedHypotheses: uniq(prop.addedHypotheses, [...added.map((a) => a.id), ...addedOther]),
          strengthenedHypotheses: uniq(prop.strengthenedHypotheses, [
            ...plan.formulation.strengthenedIds,
            ...linked.strengthened,
          ]),
          weakenedHypotheses: uniq(prop.weakenedHypotheses, [...plan.formulation.weakenedIds, ...linked.weakened]),
          rejectedHypotheses: uniq(prop.rejectedHypotheses, [...plan.formulation.rejectedIds, ...linked.rejected]),
          changeReason: mergeChangeReason(prop.changeReason, plan.formulation.changeReason, pending),
        },
        'ai-import',
      );
    }

    // 진단적 고려 개정안
    if (plan.diagnosis) {
      const prop = await this.svc.createDiagnosisProposal(session.clientId, [session.id]);
      const byName = new Map(prop.considerations.map((c) => [c.diagnosisName, c]));
      for (const c of plan.diagnosis.considerations) {
        const prev = byName.get(c.diagnosisName);
        if (prev?.status === 'externally_confirmed') continue; // 외부 확인된 공식 진단은 AI가 바꾸지 않는다
        byName.set(c.diagnosisName, {
          id: prev?.id ?? uuidv7(),
          diagnosisName: c.diagnosisName,
          status: c.status as never,
          ...(c.criteriaToAssess.length ? { criteriaToAssess: c.criteriaToAssess } : {}),
          ...(c.differentialConsiderations.length ? { differentialConsiderations: c.differentialConsiderations } : {}),
          source: 'ai-proposal',
        });
      }
      await this.repo.update(
        'DiagnosticConsideration',
        prop.id,
        {
          considerations: [...byName.values()],
          ...(plan.diagnosis.symptomList.length ? { symptomList: plan.diagnosis.symptomList } : {}),
        },
        'ai-import',
      );
    }

    // 상담기록지 초안
    if (plan.journal && Object.keys(plan.journal.fields).length) {
      const j = (await this.svc.currentJournal(session.id)) ?? (await this.svc.createJournalDraft(session.id));
      if (j.status !== 'approved') {
        const fieldSources = {
          ...(j.fieldSources ?? {}),
          ...Object.fromEntries(Object.keys(plan.journal.fields).map((k) => [k, 'ai-draft' as const])),
        };
        await this.repo.update('CounselingJournal', j.id, { ...plan.journal.fields, fieldSources }, 'ai-import');
      }
    }

    // 상담목표: 아직 하나도 없으면 AI 제안으로 채운다 (수정·삭제는 상담자). 이미 있으면 제안만 알림
    if (plan.goalSuggestions.length) {
      const existing = (await this.svc.listGoals(session.clientId)).filter((g) => !g.deletedAt);
      if (!existing.length) {
        for (const [k, g] of plan.goalSuggestions.entries()) {
          const gid = await id(`goal-new-${k}`);
          if (await this.repo.find('CounselingGoal', gid)) continue;
          await this.repo.create(
            'CounselingGoal',
            {
              clientId: session.clientId,
              title: g.title,
              agreedWithClient: g.agreedWithClient,
              startSession: session.sessionNumber,
              status: 'not_started',
              ...(g.rationale ? { description: g.rationale } : {}),
            },
            'ai-import',
            { id: gid },
          );
        }
        warnings.push(`상담목표 ${plan.goalSuggestions.length}개를 AI 제안으로 추가했습니다 (목표 탭에서 확인·수정)`);
      } else {
        const have = new Set(existing.map((g) => g.title.replace(/\s+/g, '')));
        const fresh = plan.goalSuggestions.filter((g) => !have.has(g.title.replace(/\s+/g, '')));
        if (fresh.length)
          warnings.push(`AI가 제안한 새 목표 (추가하려면 목표 탭에서): ${fresh.map((g) => g.title).join(' / ')}`);
      }
    }

    // 내담자 정보 덧붙이기 (확실한 내담자 발언만, 기존 내용은 그대로 두고 추가)
    const additions = plan.profile
      .filter((p) => p.ok)
      .map((p) => ({ field: p.field, value: p.value, ...(p.changed ? { changed: true } : {}) }));
    if (additions.length) {
      const client = await this.svc.getClient(session.clientId);
      const intake = await this.svc.ensureIntake(session.clientId);
      const merged = mergeProfile(client, intake, additions, session.sessionNumber);
      if (Object.keys(merged.client).length) await this.repo.update('Client', client.id, merged.client, 'ai-import');
      if (Object.keys(merged.intake).length)
        await this.repo.update('IntakeSummary', intake.id, merged.intake, 'ai-import');
      if (merged.applied.length)
        warnings.push(
          `내담자 정보에 추가: ${merged.applied.map((x) => `${PROFILE_FIELDS[x.field].label} — ${x.value}`).join(' / ')}`,
        );
    }

    // 화자 검토 (자동 판정 줄만, 신뢰도 상한)
    if (plan.roleReviews.length) {
      const w = await this.ts.getWorking(session);
      if (w) {
        const map = new Map(plan.roleReviews.map((r) => [r.segmentId, r.role]));
        const segments = w.segments.map((s) =>
          map.has(s.segmentId) && s.roleSource !== 'user'
            ? {
                ...s,
                clinicalRole: map.get(s.segmentId)!,
                roleSource: 'ai-review' as const,
                roleConfidence: Math.min(AI_ROLE_CONFIDENCE_CAP, Math.max(s.roleConfidence, 0.75)),
              }
            : s,
        );
        await this.repo.update('Transcript', w.id, { segments }, 'ai-import');
      }
    }

    // 축어록 추천 (후보): 다시 넣으면 이전 AI 후보(채택 안 한 것)는 새 후보로 바뀐다. 채택한 구간은 그대로
    let replacedDialogue: 'replaced' | 'kept' | null = null;
    const newVb0 = await id('vb0');
    if (plan.verbatims.length) {
      const oldCands = (await this.repo.list('VerbatimSelection', { sessionId: session.id })).filter(
        (v) => !v.deletedAt && v.proposedBy === 'ai' && v.status === 'candidate' && v.id !== newVb0,
      );
      const myIds = new Set(await Promise.all(plan.verbatims.map((_, i) => id(`vb${i}`))));
      const docs = (await this.repo.list('DocumentArtifact', { clientId: session.clientId })).filter(
        (d) => !d.deletedAt,
      );
      for (const v of oldCands) {
        if (myIds.has(v.id)) continue;
        // 이 후보로 자동 생성된 대화록: 손대지 않았으면 지우고 새로, 수정했으면 그대로 둔다
        const dlg = docs.find(
          (d) => d.documentType === 'dialogue' && d.verbatimSelectionId === v.id && d.status !== 'approved',
        );
        if (dlg) {
          if (dlg.revision <= 2 && dlg.updatedBy === 'ai-import') {
            await this.repo.softDelete('DocumentArtifact', dlg.id);
            replacedDialogue = 'replaced';
          } else {
            replacedDialogue = 'kept';
            continue; // 상담자가 고친 대화록의 구간은 남긴다
          }
        }
        await this.repo.softDelete('VerbatimSelection', v.id);
      }
    }
    for (const [i, v] of plan.verbatims.entries()) {
      const vid = await id(`vb${i}`);
      if (await this.repo.find('VerbatimSelection', vid)) continue;
      await this.repo.create(
        'VerbatimSelection',
        {
          clientId: session.clientId,
          sessionId: session.id,
          startMs: Math.max(0, Math.round(v.startMs)),
          endMs: Math.max(0, Math.round(v.endMs)),
          segmentIdsSnapshot: v.segmentIds,
          ...(v.reason ? { selectionReason: v.reason } : {}),
          ...(v.supervisionQuestions.length ? { supervisionQuestions: v.supervisionQuestions } : {}),
          processNotes: 'AI 변화 지점 추천',
          proposedBy: 'ai',
          status: 'candidate',
        },
        'ai-import',
        { id: vid },
      );
    }

    // 대화록 초안: 첫 번째 변화 지점 구간으로 자동 생성 (채택·수정은 상담자). 이미 만들었으면 다시 만들지 않음
    if (plan.verbatims.length && Object.keys(plan.dialogueDraft).length && replacedDialogue !== 'kept') {
      const vid = await id('vb0');
      const docs = await this.repo.list('DocumentArtifact', { clientId: session.clientId });
      if (!docs.some((d) => d.documentType === 'dialogue' && d.verbatimSelectionId === vid)) {
        const doc = await this.svc.createDialogueRecord(vid);
        const r = applyDocDraft(doc.content, 'dialogue', { fields: plan.dialogueDraft, cautions: [] });
        const fieldSources = {
          ...(doc.fieldSources ?? {}),
          ...Object.fromEntries(r.applied.map((k) => [`ai.${k}`, 'needs-review' as const])),
        };
        await this.repo.update('DocumentArtifact', doc.id, { content: r.content, fieldSources }, 'ai-import');
        warnings.push(
          replacedDialogue === 'replaced'
            ? '대화록 초안을 새 결과로 다시 만들었습니다 (이전 초안은 지움)'
            : '대화록 초안을 만들었습니다 (내담자 상세 > 문서). 구간은 회기 화면에서 채택·변경할 수 있습니다',
        );
      }
    }

    // 자기 점검 → 수퍼비전 노트 (Claude 2차 검토를 같은 응답에서)
    if (plan.selfReview) {
      const r = plan.selfReview;
      const join = (xs: string[]) => xs.map((x) => `- ${x}`).join('\n') || undefined;
      const fb = join([
        ...r.unsupportedInference,
        ...r.missingInformation,
        ...r.contradictoryEvidence,
        ...r.theoryBias,
      ]);
      const nid = await id('selfReview');
      for (const n of await this.repo.list('SupervisionNote', { sessionId: session.id }))
        if (
          !n.deletedAt &&
          n.id !== nid &&
          n.source === 'claude-review' &&
          n.createdBy === 'ai-import' &&
          n.revision === 1
        )
          await this.repo.softDelete('SupervisionNote', n.id);
      if (!(await this.repo.find('SupervisionNote', nid)))
        await this.repo.create(
          'SupervisionNote',
          {
            clientId: session.clientId,
            sessionId: session.id,
            source: 'claude-review',
            ...(join(r.supervisionQuestions) ? { supervisionQuestion: join(r.supervisionQuestions)! } : {}),
            ...(join(r.interventionConcerns) ? { counselorDifficulty: join(r.interventionConcerns)! } : {}),
            ...(join(r.alternativeHypotheses) ? { alternativeIntervention: join(r.alternativeHypotheses)! } : {}),
            ...(fb ? { feedbackReceived: fb } : {}),
          },
          'ai-import',
          { id: nid },
        );
    }

    session = await this.svc.getSession(session.id);
    await this.repo.update(
      'Session',
      session.id,
      {
        ...(session.status === 'ai_pending' || session.status === 'verification' ? { status: 'review' as const } : {}),
        processingState: { ...session.processingState, aiPacketCreated: true, aiAnalysisImported: true },
      },
      'system',
    );
    await this.repo.update('AIImport', importId, { appliedAt: new Date().toISOString() }, 'system');
    return { applied: true, warnings };
  }

  /** 다음 회기 Brief에 합칠 AI 제안 (가장 최근에 반영된 결과) */
  async latestAppliedAnalysis(sessionId: string): Promise<SessionAnalysisV1 | null> {
    const imp = (await this.listImports(sessionId))
      .filter((x) => x.purpose === 'session-analysis' && x.appliedAt)
      .at(-1);
    if (!imp) return null;
    const p = parseSessionAnalysis(imp.rawOutput);
    return p.ok ? p.value : null;
  }

  // ---------------- 문서 AI 초안 (모든 문서, A27) ----------------

  /** 식별정보 지우기: 실명 자리표시자·별칭·상담자 이름 → 코드/역할 */
  private async sanitize(text: string, clientId: string): Promise<string> {
    const client = await this.svc.getClient(clientId);
    const counselor = (await this.svc.getSettings())?.counselorName;
    let t = text.split(NAME_PLACEHOLDER).join('(내담자)');
    if (client.alias?.trim()) t = t.split(client.alias.trim()).join(client.clientCode);
    if (counselor?.trim()) t = t.split(counselor.trim()).join('(상담자)');
    return t;
  }

  /** 문서에 맞는 추가 자료: 회기 문서는 그 회기의 전사 전체, 내담자 문서는 회기별 요약·진술·개입 */
  private async docExtras(doc: DocumentArtifact): Promise<string> {
    const out: string[] = [];
    for (const t of await this.svc.listAssessments(doc.clientId))
      out.push(
        `[심리검사] ${t.testName}${t.administeredAt ? ` (${t.administeredAt})` : ''}: ${t.scoresSummary ?? ''}${t.interpretation ? `\n해석${t.interpretationApprovedAt ? '' : '(상담자 확인 전)'}: ${flatInterpretation(t.interpretation)}` : ''}`,
      );
    // 지금 사례개념화 (보고서를 만든 뒤 다시 세웠어도 III장을 지금 것으로 쓰게): 승인된 최신, 없으면 진행 중인 개정안
    if (doc.documentType === 'case_report' || doc.documentType === 'termination_report') {
      const f =
        (await this.svc.currentFormulation(doc.clientId)) ??
        (await this.svc.listFormulationRevisions(doc.clientId)).find((r) => r.status === 'proposal');
      if (f) {
        const rows = FORMULATION_GROUPS.flatMap((g) => g.fields)
          .map((k) => {
            const v = f[k as keyof typeof f] as unknown;
            const items = Array.isArray(v)
              ? v.map((x) => (typeof x === 'string' ? x : (x as { text: string }).text)).filter((t) => t?.trim())
              : [];
            return items.length ? `- ${FORMULATION_LABELS[k]}: ${items.join(' / ')}` : '';
          })
          .filter(Boolean);
        if (rows.length)
          out.push(
            `[지금 사례개념화 v${f.version}${f.status === 'approved' ? '' : ' (개정안)'}] III장(사례개념화)과 목표·전략은 이것을 기준으로 쓴다 (문서에 남아 있는 예전 내용보다 우선)\n${rows.join('\n')}`,
          );
      }
    }
    const tp = (await this.svc.getClient(doc.clientId)).treatmentPlan;
    const main = approachById(tp?.approach ?? '');
    if (main && tp) {
      const step = main.steps[Math.min(tp.currentStep, main.steps.length - 1)];
      out.push(
        `[주 이론] ${main.name} (${main.scholar}) — 상담자가 채택한 치료 방향이다. 사례개념화·개입·평가를 이 이론의 틀로 일관되게 쓰고, 다른 이론 이름을 늘어놓지 않는다.${tp.status === 'completed' ? ' (치료 완료로 표시됨)' : step ? ` 지금 단계: ${step.title}.` : ''}${secondaryOf(tp) ? ` 보조 접근: ${secondaryOf(tp)!.approach.name}${secondaryOf(tp)!.role ? `(쓰는 자리: ${secondaryOf(tp)!.role})` : ''} — 개입 부분에서 주 이론의 틀 안에서 쓴 보조 기법으로만 짧게 밝힌다.` : ''}`,
      );
    }
    // 상담자가 직접 적은 성찰 메모 (보고서를 만든 뒤에 적은 것도 함께)
    if (doc.documentType === 'case_report' || doc.documentType === 'termination_report') {
      const notes = await this.svc.listReflections(doc.clientId);
      const pick = (k: string) =>
        notes.reflections.filter((r) => r.kind === k && r.text.trim()).map((r) => r.text.trim());
      const sup = notes.supervision
        .map((n) => (n.supervisionQuestion ?? n.counselorDifficulty ?? '').trim())
        .filter(Boolean);
      const lines = [
        ...pick('self-understanding').map((t) => `- 상담자 자신에 대한 이해: ${t}`),
        ...sup.map((t) => `- 수퍼비전에서 도움 받고 싶은 점: ${t}`),
        ...pick('theological-reflection').map((t) => `- 신학적 성찰: ${t}`),
      ];
      if (lines.length)
        out.push(
          `[상담자 메모] 상담자가 직접 적은 성찰이다. IV(상담자 자신에 대한 이해)·VII(수퍼비전)·VIII(신학적 성찰) 칸의 재료로, 상담자의 생각과 표현을 살려 문단으로 다듬는다 (새 성찰을 지어내지 않는다).\n${lines.join('\n')}`,
        );
    }
    const sessionLevel =
      doc.documentType === 'session_record' || doc.documentType === 'verbatim' || doc.documentType === 'dialogue';
    const sessions = (await this.svc.listSessions(doc.clientId)).filter((s) =>
      sessionLevel
        ? s.id === doc.sessionId
        : doc.documentType === 'case_report' && doc.includedSessionIds?.length
          ? doc.includedSessionIds.includes(s.id) // 사례보고서는 만들 때 고른 회기만
          : (doc.includedSessionIds ?? []).includes(s.id) || s.status === 'approved',
    );
    const [evidence, goals, progress] = await Promise.all([
      this.repo.list('ClinicalEvidence', { clientId: doc.clientId }),
      this.svc.listGoals(doc.clientId),
      this.svc.listGoalProgress({ clientId: doc.clientId }),
    ]);
    for (const s of sessions.sort((a, b) => a.sessionNumber - b.sessionNumber)) {
      const [analysis, memo, journal] = await Promise.all([
        this.svc.getAnalysis(s.id),
        this.svc.getMemo(s.id),
        this.svc.currentJournal(s.id),
      ]);
      out.push(`[S${s.sessionNumber} · ${s.sessionDate} · ${s.durationMinutes ?? '-'}분]`);
      if (analysis?.sessionSummary) out.push(`요약: ${analysis.sessionSummary}`);
      if (analysis?.keyEvents?.length) out.push(`주요 사건: ${analysis.keyEvents.join(' / ')}`);
      const items = [
        ...(analysis?.emotions ?? []),
        ...(analysis?.cognitions ?? []),
        ...(analysis?.behaviors ?? []),
        ...(analysis?.relationshipPatterns ?? []),
      ];
      if (items.length)
        out.push(
          `정서·인지·행동·관계: ${items.map((x) => `${x.text}${x.status === 'hypothesis' ? '(가설)' : ''}`).join(' / ')}`,
        );
      for (const iv of analysis?.interventions ?? [])
        out.push(
          `개입: ${iv.interventionName}${iv.theoreticalOrientation ? ` (${iv.theoreticalOrientation})` : ''}${iv.clientResponse ? ` → 반응: ${iv.clientResponse}` : ''}`,
        );
      if (memo?.text.trim()) out.push(`상담자 메모: ${memo.text.trim()}`);
      if (journal?.counselingContent) out.push(`상담기록지 상담 내용: ${journal.counselingContent}`);
      const reports = evidence.filter(
        (e) => e.sessionId === s.id && e.evidenceType === 'CLIENT_REPORT' && !e.deletedAt,
      );
      if (reports.length)
        out.push(`내담자 진술:\n${reports.map((e) => `- ${e.text}${e.verified ? '' : ' (확인 전)'}`).join('\n')}`);
      if (sessionLevel && s.workingTranscriptId) {
        const w = await this.repo.find('Transcript', s.workingTranscriptId);
        if (w?.segments.length) out.push(TRANSCRIPT_LEGEND, ...transcriptLines(w.segments));
      }
      if (sessionLevel) {
        const brief = await this.svc.previousBriefFor(s);
        if (brief?.sessionObjectives?.length)
          out.push(`이번 회기 전에 세운 계획: ${brief.sessionObjectives.join(' / ')}`);
      }
      out.push('');
    }
    const live = goals.filter((g) => !g.deletedAt);
    if (live.length)
      out.push(
        `[상담 목표]\n${live
          .map(
            (g) =>
              `- ${g.title} (${g.agreedWithClient ? '합의' : '임상적'}, 현재 ${g.status})${progress
                .filter((p) => p.goalId === g.id && p.progressNote)
                .map((p) => ` / ${p.progressNote}`)
                .join('')}`,
          )
          .join('\n')}`,
      );
    return out.join('\n');
  }

  private async draftDoc(documentId: string) {
    return this.repo.get('DocumentArtifact', documentId);
  }

  /** 분량 목표 → 서술 칸 글자 예산 (A4 한 쪽 약 1,500자, 표·축어록·잠긴 칸이 차지하는 만큼 뺀다) */
  private lengthFor(content: string, type: WholeDocType, pages?: string) {
    const target = pages?.trim();
    if (!target) return undefined;
    const nums = (target.match(/\d+(\.\d+)?/g) ?? []).map(Number).filter((n) => n > 0);
    if (!nums.length) return { pages: target };
    const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
    let fixed = 0;
    let fields = 0;
    for (const b of parseDoc(content).blocks) {
      if (b.kind !== 'field' || b.label.startsWith('별지')) continue; // 별지는 분량에 넣지 않는다
      if (isLockedHeading(type, b.label) || isVerbatimLabel(b.label)) {
        fixed += b.lines.join('').length;
        continue;
      }
      fixed += b.lines.filter((l) => l.trim().startsWith('|')).join('').length;
      // 써야 하는 서술 칸 (세부 칸만 거느리는 머리 칸은 빼고, 지금 빈 칸도 모두 셈)
      if (b.lines.length) fields++;
    }
    const prose = Math.round((avg * 1500 - fixed * 1.3) / 100) * 100;
    return {
      pages: target,
      ...(prose >= 2000 ? { proseChars: prose } : {}),
      ...(fields ? { proseFields: fields } : {}),
    };
  }

  async docDraftPacket(documentId: string, opts: { pages?: string } = {}): Promise<string> {
    const doc = await this.draftDoc(documentId);
    const TITLE: Record<DocumentArtifact['documentType'], string> = {
      session_record: '상담기록지',
      case_report: '상담사례 보고서',
      termination_report: '상담 종결 보고서',
      verbatim: '축어록',
      dialogue: '대화록(Verbatim)',
    };
    if (isWholeDocType(doc.documentType)) {
      // 사례보고서·종결보고서: 문서 전체를 한 번에 (A30)
      return buildWholeDocPacket({
        type: doc.documentType,
        title: TITLE[doc.documentType],
        content: await this.sanitize(normalizeReportFormat(doc.content, doc.documentType), doc.clientId),
        extras: await this.sanitize(await this.docExtras(doc), doc.clientId),
        guidelines: await this.writingGuidelines(),
        mustInclude: await this.mustIncludeFor(doc),
        ...(() => {
          const length = this.lengthFor(doc.content, doc.documentType as WholeDocType, opts.pages);
          return length ? { length } : {};
        })(),
      });
    }
    return buildDocDraftPacket({
      type: doc.documentType,
      title: TITLE[doc.documentType],
      content: await this.sanitize(doc.content, doc.clientId),
      extras: await this.sanitize(await this.docExtras(doc), doc.clientId),
    });
  }

  /** 지도감독 원칙·작성 기준 (설정에 쓴 것, 없으면 기본값) */
  async writingGuidelines(): Promise<string> {
    return (await this.svc.getSettings())?.writingGuidelines?.trim() || DEFAULT_SUPERVISION_GUIDELINES;
  }

  /** 보고서에 꼭 들어가야 할 항목: 상담목표, 강화·확인된 가설, 위험 정보, 심리검사, 주호소 */
  async mustIncludeFor(doc: DocumentArtifact): Promise<MustInclude[]> {
    // 한 회기 문서: 그 회기의 위험 정보만 (상담기록지). 축어록은 구간 문서라 없음
    if (doc.documentType === 'verbatim' || doc.documentType === 'dialogue') return [];
    if (doc.documentType === 'session_record') return doc.sessionId ? this.sessionRiskMustInclude(doc.sessionId) : [];
    const out: MustInclude[] = [];
    const goals = (await this.svc.listGoals(doc.clientId)).filter((g) => !g.deletedAt && g.status !== 'terminated');
    for (const g of goals.slice(0, 5))
      out.push({ label: `상담목표: ${g.title}`, terms: [g.title, ...keyTerms(g.title, 2)] });
    const f = await this.svc.currentFormulation(doc.clientId);
    if (f) {
      const strong = new Set([...(f.strengthenedHypotheses ?? [])]);
      for (const k of FORMULATION_ITEM_FIELDS)
        for (const h of f[k] ?? [])
          if (h.status === 'confirmed' || strong.has(h.id))
            out.push({ label: `가설: ${h.text}`, terms: keyTerms(h.text, 3) });
    }
    const picked = doc.documentType === 'case_report' && doc.includedSessionIds?.length ? doc.includedSessionIds : null;
    const sessions = (await this.svc.listSessions(doc.clientId)).filter((s) => !picked || picked.includes(s.id));
    const present = new Set<RiskKey>();
    for (const s of sessions) {
      const risk = await this.svc.getRisk(s.id);
      if (risk) for (const k of RISK_KEYS) if (risk[k]?.status === 'present') present.add(k);
    }
    for (const k of present)
      out.push({ label: `위험: ${RISK_LABELS[k]}`, terms: RISK_TERMS[k] ?? [RISK_LABELS[k]], critical: true });
    const tests = (await this.repo.list('PsychologicalAssessment', { clientId: doc.clientId })).filter(
      (a) => !a.deletedAt,
    );
    for (const t of tests) out.push({ label: `심리검사: ${t.testName}`, terms: [t.testName] });
    const main = approachById((await this.svc.getClient(doc.clientId)).treatmentPlan?.approach ?? '');
    if (main) out.push({ label: `주 이론: ${main.name} (${main.scholar})`, terms: [main.name, ...main.aliases] });
    const intake = await this.svc.getIntake(doc.clientId);
    for (const pp of (intake?.presentingProblems ?? []).slice(0, 3))
      out.push({ label: `주호소: ${pp}`, terms: keyTerms(pp, 3) });
    return out.slice(0, 14);
  }

  /** 한 회기의 위험 정보 (상담기록지에 꼭 들어가야 함) */
  private async sessionRiskMustInclude(sessionId: string): Promise<MustInclude[]> {
    const risk = await this.svc.getRisk(sessionId);
    if (!risk) return [];
    return RISK_KEYS.filter((k) => risk[k]?.status === 'present').map((k) => ({
      label: `위험: ${RISK_LABELS[k]}`,
      terms: RISK_TERMS[k] ?? [RISK_LABELS[k]],
      critical: true,
    }));
  }

  // ---------------- 상담기록지 다듬기 (승인 전, 기록지 화면) ----------------

  /** 기록지 칸을 문서 모양(### 칸 제목 + 본문)으로 */
  private async journalAsDoc(journalId: string) {
    const j = await this.repo.get('CounselingJournal', journalId);
    const s = await this.svc.getSession(j.sessionId);
    const c = await this.svc.getClient(s.clientId);
    const content = [
      `# 상담기록지 — ${c.clientCode} ${s.sessionNumber}회기`,
      ...JOURNAL_FORM_FIELDS.flatMap((k) => [
        `### ${JOURNAL_FIELD_LABELS[k]}`,
        j[k]?.trim() || '[상담자 직접 작성 필요]',
        '',
      ]),
    ].join('\n');
    return { j, s, c, content };
  }

  async journalPolishPacket(journalId: string, request?: string): Promise<string> {
    const { j, s, content } = await this.journalAsDoc(journalId);
    const fake = { documentType: 'session_record', sessionId: s.id, clientId: s.clientId } as DocumentArtifact;
    return buildWholeDocPacket({
      type: 'session_record',
      title: '상담기록지',
      content: await this.sanitize(content, j.clientId),
      extras: await this.sanitize(await this.docExtras(fake), j.clientId),
      guidelines: await this.writingGuidelines(),
      mustInclude: await this.sessionRiskMustInclude(s.id),
      ...(request ? { request } : {}),
    });
  }

  /** 다듬은 답 미리보기: 칸별 지금 → 새 내용 (잠긴 칸은 빼고), 빠진 위험 정보 */
  async previewJournalPolish(
    journalId: string,
    raw: string,
  ): Promise<
    | { ok: false; errors: string[] }
    | {
        ok: true;
        changes: { heading: string; before: string; after: string }[];
        locked: string[];
        missing: MustInclude[];
      }
  > {
    const { j, s, content } = await this.journalAsDoc(journalId);
    // 기록지 칸 제목과 같은 제목이 하나라도 있으면 그 칸만 고친 답으로 본다 (보충 답은 칸이 한두 개)
    const md = draftFromMarkdown(raw, content)?.revise ?? sectionsByHeadings(raw, Object.values(JOURNAL_FIELD_LABELS));
    if (!Object.keys(md).length) return { ok: false, errors: ['기록지 형식(### 칸 제목)이 아닙니다'] };
    const prep = prepareWholeDocRevise('session_record', content, md);
    const changes: { heading: string; before: string; after: string }[] = [];
    for (const k of JOURNAL_FORM_FIELDS) {
      const h = JOURNAL_FIELD_LABELS[k];
      const after = prep.revise[h]?.trim();
      const before = (j[k] ?? '').trim();
      if (after && after !== before) changes.push({ heading: h, before, after });
    }
    const merged = JOURNAL_FORM_FIELDS.map(
      (k) => changes.find((c) => c.heading === JOURNAL_FIELD_LABELS[k])?.after ?? j[k] ?? '',
    ).join('\n');
    const missing = missingItems(merged, await this.sessionRiskMustInclude(s.id));
    return { ok: true, changes, locked: prep.locked, missing };
  }

  /** 고른 칸만 기록지에 넣는다 (원문은 AIImport에 보존). 승인된 기록지는 바꾸지 않음 */
  async applyJournalPolish(journalId: string, raw: string, only: string[], provider: AIImport['provider'] = 'claude') {
    const pv = await this.previewJournalPolish(journalId, raw);
    const j = await this.repo.get('CounselingJournal', journalId);
    await this.repo.create(
      'AIImport',
      {
        clientId: j.clientId,
        sessionId: j.sessionId,
        provider,
        purpose: 'other',
        packetVersion: 'WHOLE_DOC_PACKET_V1',
        outputFormatVersion: 'JOURNAL_POLISH_V1',
        promptVersion: 'WHOLE_DOC_PACKET_V1',
        rawOutput: raw,
        parseStatus: pv.ok ? 'parsed' : 'failed',
        ...(pv.ok ? {} : { parseErrors: pv.errors }),
      },
      'user',
    );
    if (!pv.ok) throw new Error(pv.errors.join(', '));
    if (j.status === 'approved') throw new Error('승인된 기록지는 바꿀 수 없습니다. 개정본을 만드세요.');
    const keep = new Set(only);
    const patch: Record<string, string> = {};
    for (const c of pv.changes) {
      if (!keep.has(c.heading)) continue;
      const k = JOURNAL_FORM_FIELDS.find((f) => JOURNAL_FIELD_LABELS[f] === c.heading);
      if (k) patch[k] = c.after;
    }
    if (!Object.keys(patch).length) return { applied: 0 };
    await this.svc.updateJournal(journalId, patch, Object.keys(patch));
    return { applied: Object.keys(patch).length };
  }

  /**
   * 붙여넣은 답 미리보기 (저장하지 않음): 칸별로 지금 → 새 내용, 잠겨서 빼는 칸, 꼭 넣을 항목 중 빠진 것.
   * 사례보고서·종결보고서에서 반영 전에 칸마다 받기/유지를 고르게 한다.
   */
  async previewDocDraft(
    documentId: string,
    raw: string,
  ): Promise<
    | { ok: false; errors: string[] }
    | {
        ok: true;
        changes: { heading: string; before: string; after: string }[];
        locked: string[];
        missing: MustInclude[];
      }
  > {
    const doc = await this.draftDoc(documentId);
    const base = isWholeDocType(doc.documentType) ? normalizeReportFormat(doc.content, doc.documentType) : doc.content;
    const draft = this.readDocDraft(raw, doc.documentType, base);
    if (!draft.ok) return { ok: false, errors: draft.errors };
    const r = applyDocDraft(base, doc.documentType, draft.value);
    const body = (content: string, h: string) =>
      (fieldBody(content, h) ?? '')
        .split('\n')
        .filter((l) => !isAiDraftMark(l.trim()))
        .join('\n')
        .trim();
    const changes = r.changes.map((c) => ({
      heading: c.heading,
      before: body(base, c.heading),
      after: body(r.content, c.heading),
    }));
    const missing = isWholeDocType(doc.documentType) ? missingItems(r.content, await this.mustIncludeFor(doc)) : [];
    return { ok: true, changes, locked: draft.locked, missing };
  }

  /** 답 읽기: 보고서 전체(마크다운) 또는 JSON. 사례보고서·종결보고서는 잠긴 칸 빼고, 전사 번호 지우고, 이름 가림 */
  private readDocDraft(
    raw: string,
    type: DocumentArtifact['documentType'],
    content: string,
  ): { ok: true; value: DocDraft; locked: string[] } | { ok: false; errors: string[] } {
    const few = sectionsByHeadings(raw, fieldHeadings(content));
    const md =
      draftFromMarkdown(raw, content) ?? (Object.keys(few).length ? { fields: {}, cautions: [], revise: few } : null);
    const p = md ? ({ ok: true, value: md } as const) : parseDocDraft(raw, type);
    if (!p.ok) return { ok: false, errors: p.errors };
    if (!isWholeDocType(type)) return { ok: true, value: p.value, locked: [] };
    // JSON 항목(fields)도 칸 제목으로 옮겨 같은 정리를 거친다
    const byHeading: Record<string, string> = { ...(p.value.revise ?? {}) };
    for (const [k, v] of Object.entries(p.value.fields)) {
      const spec = DOC_DRAFT_SPECS[type].find((x) => x.key === k);
      const h = spec ? (spec.mode === 'fill' ? spec.heading.replace(/^#+\s*/, '') : spec.label) : undefined;
      if (h && !(h in byHeading)) byHeading[h] = v;
    }
    const prep = prepareWholeDocRevise(type, content, byHeading);
    return { ok: true, value: { fields: {}, cautions: p.value.cautions, revise: prep.revise }, locked: prep.locked };
  }

  /** 원문 보존 → 파싱 → 붙여넣은 답대로 칸을 채우고 고친다 (승인된 문서는 바꾸지 않음) */
  async importDocDraft(
    documentId: string,
    raw: string,
    provider: AIImport['provider'] = 'claude',
    opts: { only?: string[] } = {},
  ): Promise<{
    importId: string;
    ok: boolean;
    errors?: string[];
    warnings?: string[];
    applied?: number;
    revised?: number;
  }> {
    const doc = await this.draftDoc(documentId);
    const imp = await this.repo.create(
      'AIImport',
      {
        clientId: doc.clientId,
        ...(doc.sessionId ? { sessionId: doc.sessionId } : {}),
        documentId,
        provider,
        purpose: 'report-draft',
        packetVersion: DOC_DRAFT_VERSIONS.packet,
        outputFormatVersion: DOC_DRAFT_VERSIONS.output,
        promptVersion: DOC_DRAFT_VERSIONS.task,
        rawOutput: raw,
        parseStatus: 'pending',
      },
      'user',
    );
    // JSON이 아니어도 보고서 전체(마크다운)를 붙여넣으면 칸별로 반영
    const whole = isWholeDocType(doc.documentType);
    const withFields = (c: string) =>
      isWholeDocType(doc.documentType) ? normalizeReportFormat(c, doc.documentType) : c;
    const p = this.readDocDraft(raw, doc.documentType, withFields(doc.content));
    if (p.ok && opts.only) {
      // 상담자가 고른 칸만 반영
      const keep = new Set(opts.only);
      p.value.revise = Object.fromEntries(Object.entries(p.value.revise ?? {}).filter(([h]) => keep.has(h.trim())));
      if (whole) p.value.fields = {};
    }
    if (!p.ok) {
      await this.repo.update('AIImport', imp.id, { parseStatus: 'failed', parseErrors: p.errors }, 'system');
      return { importId: imp.id, ok: false, errors: p.errors };
    }
    const cur = await this.repo.get('DocumentArtifact', documentId);
    if (cur.status === 'approved') throw new Error('승인된 문서는 바꿀 수 없습니다. 개정본을 만드세요.');
    // Claude가 보낸 칸은 Claude가 쓴 대로 (빈 칸은 채우고 내용이 있는 칸은 고침). 보내지 않은 칸은 그대로
    const r = applyDocDraft(withFields(cur.content), cur.documentType, p.value);
    const fieldSources = {
      ...(cur.fieldSources ?? {}),
      ...Object.fromEntries(r.applied.map((k) => [`ai.${k}`, 'needs-review' as const])),
    };
    const warnings = [
      ...(r.changes.length
        ? [`반영한 칸 ${r.changes.length}개: ${r.changes.map((c) => c.heading).join(' / ')}`]
        : ['바뀐 칸이 없습니다 (외부 답이 지금 문서와 같음)']),
      ...(r.notFound.length ? [`AI가 고치려 한 칸을 문서에서 찾지 못함: ${r.notFound.join(', ')}`] : []),
      ...(p.locked.length ? [`앱이 채우는 칸이라 반영하지 않음: ${p.locked.join(', ')}`] : []),
      ...p.value.cautions.map((c) => `AI 주의: ${c}`),
    ];
    await this.repo.update('DocumentArtifact', documentId, { content: r.content, fieldSources }, 'ai-import', {
      expectedRevision: cur.revision,
    });
    await this.repo.update(
      'AIImport',
      imp.id,
      {
        parseStatus: 'parsed',
        appliedAt: new Date().toISOString(),
        ...(warnings.length ? { parseWarnings: warnings } : {}),
      },
      'system',
    );
    return {
      importId: imp.id,
      ok: true,
      warnings,
      applied: r.applied.length,
      revised: r.revised.length,
    };
  }

  async docDraftImports(documentId: string) {
    const doc = await this.repo.get('DocumentArtifact', documentId);
    return (await this.repo.list('AIImport', { clientId: doc.clientId }))
      .filter((x) => x.documentId === documentId)
      .sort(byCreated);
  }

  // ---------------- Claude 프로젝트 지식 파일 ----------------

  /** 내담자별 사례현황 파일 (이니셜 코드·나이 범위 등만, 실명·연락처·생년월일 없음) */
  async caseStatusFile(clientId: string): Promise<KnowledgeFile> {
    const input = await this.svc.caseReportInput(clientId, new Date().toISOString(), { draft: true });
    const c = input.client;
    const age = c.birthYear
      ? ageText({ birthYear: c.birthYear, on: new Date().toISOString().slice(0, 10) })
      : c.ageRange;
    return buildCaseStatusFile({ ...input, ...(age ? { ageText: age } : {}) });
  }

  // ---------------- Claude 2차 검토 ----------------

  async secondReviewPacket(sessionId: string, counselorQuestions?: string): Promise<string | null> {
    const session = await this.svc.getSession(sessionId);
    const analysis = await this.svc.getAnalysis(sessionId);
    if (!analysis) return null;
    const [client, formulation, goals, evidence, ai] = await Promise.all([
      this.svc.getClient(session.clientId),
      this.svc.currentFormulation(session.clientId),
      this.svc.listGoals(session.clientId),
      this.repo.list('ClinicalEvidence', { sessionId }),
      this.latestAppliedAnalysis(sessionId),
    ]);
    return buildSecondReviewPacket({
      client,
      session,
      analysis,
      goals,
      evidence,
      recommended: (ai?.recommendedInterventions ?? []).map((r) =>
        [r.intervention, r.orientation, r.rationale].filter(Boolean).join(' · '),
      ),
      ...(formulation ? { formulation } : {}),
      ...(counselorQuestions ? { counselorQuestions } : {}),
    });
  }

  async importSecondReview(
    sessionId: string,
    raw: string,
  ): Promise<{ importId: string; ok: boolean; review?: SecondReviewV1; errors?: string[] }> {
    const session = await this.svc.getSession(sessionId);
    const imp = await this.repo.create(
      'AIImport',
      {
        clientId: session.clientId,
        sessionId,
        provider: 'claude',
        purpose: 'second-review',
        packetVersion: PROMPT_VERSIONS.secondReviewPacket,
        outputFormatVersion: PROMPT_VERSIONS.secondReview,
        promptVersion: PROMPT_VERSIONS.secondReviewTask,
        rawOutput: raw,
        parseStatus: 'pending',
      },
      'user',
    );
    const p = parseSecondReview(raw);
    if (!p.ok) {
      await this.repo.update('AIImport', imp.id, { parseStatus: 'failed', parseErrors: p.errors }, 'system');
      return { importId: imp.id, ok: false, errors: p.errors };
    }
    const r = p.value;
    const join = (xs: string[]) => xs.map((x) => `- ${x}`).join('\n') || undefined;
    await this.repo.create(
      'SupervisionNote',
      {
        clientId: session.clientId,
        sessionId,
        source: 'claude-review',
        ...(join(r.supervisionQuestions) ? { supervisionQuestion: join(r.supervisionQuestions)! } : {}),
        ...(join(r.interventionConcerns) ? { counselorDifficulty: join(r.interventionConcerns)! } : {}),
        ...(join(r.alternativeHypotheses) ? { alternativeIntervention: join(r.alternativeHypotheses)! } : {}),
        ...(join([...r.unsupportedInference, ...r.missingInformation, ...r.contradictoryEvidence, ...r.theoryBias])
          ? {
              feedbackReceived: join([
                ...r.unsupportedInference,
                ...r.missingInformation,
                ...r.contradictoryEvidence,
                ...r.theoryBias,
              ])!,
            }
          : {}),
      },
      'ai-import',
      { id: await stableId(`ai|${imp.id}|review`, Date.parse(imp.createdAt)) },
    );
    await this.repo.update(
      'AIImport',
      imp.id,
      { parseStatus: 'parsed', appliedAt: new Date().toISOString() },
      'system',
    );
    return { importId: imp.id, ok: true, review: r };
  }

  async secondReviews(sessionId: string): Promise<SecondReviewV1[]> {
    return (await this.listImports(sessionId))
      .filter((x) => x.purpose === 'second-review' && x.parseStatus === 'parsed')
      .map((x) => parseSecondReview(x.rawOutput))
      .flatMap((p) => (p.ok ? [p.value] : []));
  }
}
